/**
 * Enhanced Error Handler for Neurula Health EMR
 * 
 * Provides comprehensive error handling for API responses, RBAC operations,
 * and user-friendly error messaging with proper logging.
 */

import toast from 'react-hot-toast';

// Error types for categorization
export const ERROR_TYPES = {
    AUTHENTICATION: 'AUTHENTICATION',
    AUTHORIZATION: 'AUTHORIZATION',
    RBAC: 'RBAC',
    VALIDATION: 'VALIDATION',
    NETWORK: 'NETWORK',
    SERVER: 'SERVER',
    CLIENT: 'CLIENT',
    UNKNOWN: 'UNKNOWN'
};

// Error severity levels
export const ERROR_SEVERITY = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
};

/**
 * Determine error type from response
 * @param {Object} error - Error object
 * @returns {string} Error type
 */
export const getErrorType = (error) => {
    const status = error?.response?.status || error?.status;
    const message = error?.response?.data?.detail || error?.message || '';
    
    if (status === 401 || message.toLowerCase().includes('unauthorized')) {
        return ERROR_TYPES.AUTHENTICATION;
    }
    
    if (status === 403 || message.toLowerCase().includes('forbidden') || 
        message.toLowerCase().includes('permission') || message.toLowerCase().includes('access denied')) {
        return ERROR_TYPES.AUTHORIZATION;
    }
    
    if (message.toLowerCase().includes('role') || message.toLowerCase().includes('rbac') ||
        message.toLowerCase().includes('hierarchy')) {
        return ERROR_TYPES.RBAC;
    }
    
    if (status >= 400 && status < 500) {
        return ERROR_TYPES.VALIDATION;
    }
    
    if (status >= 500) {
        return ERROR_TYPES.SERVER;
    }
    
    if (error?.code === 'NETWORK_ERROR' || message.toLowerCase().includes('network')) {
        return ERROR_TYPES.NETWORK;
    }
    
    return ERROR_TYPES.UNKNOWN;
};

/**
 * Determine error severity
 * @param {string} errorType - Error type
 * @param {Object} error - Error object
 * @returns {string} Error severity
 */
export const getErrorSeverity = (errorType, error) => {
    const status = error?.response?.status || error?.status;
    
    switch (errorType) {
        case ERROR_TYPES.AUTHENTICATION:
            return ERROR_SEVERITY.HIGH;
        case ERROR_TYPES.AUTHORIZATION:
        case ERROR_TYPES.RBAC:
            return ERROR_SEVERITY.MEDIUM;
        case ERROR_TYPES.NETWORK:
            return status >= 500 ? ERROR_SEVERITY.HIGH : ERROR_SEVERITY.MEDIUM;
        case ERROR_TYPES.SERVER:
            return ERROR_SEVERITY.HIGH;
        case ERROR_TYPES.VALIDATION:
            return ERROR_SEVERITY.LOW;
        default:
            return ERROR_SEVERITY.MEDIUM;
    }
};

/**
 * Get user-friendly error message
 * @param {Object} error - Error object
 * @param {string} context - Context where error occurred
 * @returns {string} User-friendly message
 */
export const getUserFriendlyMessage = (error, context = '') => {
    const errorType = getErrorType(error);
    const status = error?.response?.status || error?.status;
    const serverMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
    
    // RBAC specific messages
    if (errorType === ERROR_TYPES.RBAC) {
        if (serverMessage?.toLowerCase().includes('hierarchy')) {
            return 'You do not have sufficient role hierarchy to perform this action.';
        }
        if (serverMessage?.toLowerCase().includes('role')) {
            return 'Role assignment or permission error. Please contact your administrator.';
        }
        return 'Access control error. Please check your permissions.';
    }
    
    // Authentication messages
    if (errorType === ERROR_TYPES.AUTHENTICATION) {
        if (status === 401) {
            return 'Your session has expired. Please log in again.';
        }
        return 'Authentication failed. Please check your credentials.';
    }
    
    // Authorization messages
    if (errorType === ERROR_TYPES.AUTHORIZATION) {
        if (context.includes('user') || context.includes('staff')) {
            return 'You do not have permission to manage users or staff.';
        }
        if (context.includes('role') || context.includes('permission')) {
            return 'You do not have permission to manage roles or permissions.';
        }
        return 'You do not have permission to perform this action.';
    }
    
    // Network messages
    if (errorType === ERROR_TYPES.NETWORK) {
        return 'Network connection error. Please check your internet connection and try again.';
    }
    
    // Server messages
    if (errorType === ERROR_TYPES.SERVER) {
        return 'Server error occurred. Please try again later or contact support.';
    }
    
    // Validation messages
    if (errorType === ERROR_TYPES.VALIDATION) {
        if (serverMessage && serverMessage.length < 200) {
            return serverMessage;
        }
        return 'Invalid data provided. Please check your input and try again.';
    }
    
    // Default message
    return serverMessage || 'An unexpected error occurred. Please try again.';
};

/**
 * Log error with context
 * @param {Object} error - Error object
 * @param {string} context - Context where error occurred
 * @param {Object} additionalData - Additional data to log
 */
export const logError = (error, context = '', additionalData = {}) => {
    const errorType = getErrorType(error);
    const severity = getErrorSeverity(errorType, error);
    
    const errorLog = {
        timestamp: new Date().toISOString(),
        context,
        errorType,
        severity,
        status: error?.response?.status || error?.status,
        message: error?.response?.data?.detail || error?.message,
        stack: error?.stack,
        url: error?.config?.url || window.location.href,
        method: error?.config?.method,
        ...additionalData
    };
    
    // Log to console with appropriate level
    if (severity === ERROR_SEVERITY.CRITICAL || severity === ERROR_SEVERITY.HIGH) {
        console.error('ERROR:', errorLog);
    } else if (severity === ERROR_SEVERITY.MEDIUM) {
        console.warn('WARNING:', errorLog);
    } else {
        console.log('INFO:', errorLog);
    }
    
    // Send to error tracking service if available
    if (window.errorTracker && typeof window.errorTracker.captureException === 'function') {
        window.errorTracker.captureException(error, {
            tags: { context, errorType, severity },
            extra: errorLog
        });
    }
};

/**
 * Handle API errors with toast notifications and enhanced retry logic
 * @param {Object} error - Error object
 * @param {string} context - Context where error occurred
 * @param {Object} options - Options for error handling
 * @returns {Object} Standardized error response
 */
export const handleApiError = (error, context = '', options = {}) => {
    const {
        showToast = true,
        logError: shouldLog = true,
        fallbackMessage = null,
        additionalData = {},
        enableRetry = false,
        retryCallback = null,
        maxRetries = 3
    } = options;
    
    const errorType = getErrorType(error);
    const severity = getErrorSeverity(errorType, error);
    const userMessage = fallbackMessage || getUserFriendlyMessage(error, context);
    
    // Enhanced error details extraction
    const errorDetails = {
        errorId: error?.response?.data?.error?.error_id || `error_${Date.now()}`,
        code: error?.response?.data?.error?.code || 'UNKNOWN_ERROR',
        type: error?.response?.data?.error?.type || errorType,
        message: error?.response?.data?.error?.message || userMessage,
        details: error?.response?.data?.error?.details || {},
        resolution_suggestions: error?.response?.data?.error?.resolution_suggestions || [],
        status: error?.response?.status,
        url: error?.config?.url,
        method: error?.config?.method
    };
    
    // Log error if enabled
    if (shouldLog) {
        logError(error, context, {
            ...additionalData,
            errorDetails,
            userAgent: navigator.userAgent,
            url: window.location.href
        });
    }
    
    // Show toast notification with enhanced options
    if (showToast) {
        const toastOptions = {
            duration: severity === ERROR_SEVERITY.HIGH ? 8000 : 5000,
            position: 'top-right',
            style: {
                maxWidth: '500px'
            }
        };
        
        // Add retry action if enabled and appropriate
        if (enableRetry && retryCallback && shouldShowRetryOption(errorType)) {
            const retryToastOptions = {
                ...toastOptions,
                action: {
                    label: 'Retry',
                    onClick: () => {
                        if (typeof retryCallback === 'function') {
                            retryCallback();
                        }
                    }
                }
            };
            
            if (severity === ERROR_SEVERITY.HIGH || severity === ERROR_SEVERITY.CRITICAL) {
                toast.error(userMessage, retryToastOptions);
            } else {
                toast.error(userMessage, retryToastOptions);
            }
        } else {
            // Regular toast without retry
            if (severity === ERROR_SEVERITY.HIGH || severity === ERROR_SEVERITY.CRITICAL) {
                toast.error(userMessage, toastOptions);
            } else if (severity === ERROR_SEVERITY.MEDIUM) {
                toast.error(userMessage, { ...toastOptions, duration: 4000 });
            } else {
                toast(userMessage, { ...toastOptions, duration: 3000 });
            }
        }
        
        // Show resolution suggestions if available
        if (errorDetails.resolution_suggestions && errorDetails.resolution_suggestions.length > 0) {
            setTimeout(() => {
                const suggestions = errorDetails.resolution_suggestions.slice(0, 2).join(' • ');
                toast.info(`Suggestions: ${suggestions}`, {
                    duration: 6000,
                    position: 'top-right',
                    style: {
                        maxWidth: '500px',
                        fontSize: '14px'
                    }
                });
            }, 1000);
        }
    }
    
    // Return standardized error response with enhanced details
    return {
        success: false,
        error: true,
        errorType,
        severity,
        message: userMessage,
        details: errorDetails,
        originalError: error,
        timestamp: new Date().toISOString(),
        canRetry: shouldShowRetryOption(errorType)
    };
};

/**
 * Determine if retry option should be shown based on error type
 * @param {string} errorType - The type of error
 * @returns {boolean} Whether retry should be offered
 */
const shouldShowRetryOption = (errorType) => {
    const retryableErrors = [
        ERROR_TYPES.NETWORK,
        ERROR_TYPES.SERVER,
        ERROR_TYPES.UNKNOWN
    ];
    return retryableErrors.includes(errorType);
};

/**
 * Handle RBAC specific errors
 * @param {Object} error - Error object
 * @param {string} operation - RBAC operation that failed
 * @param {Object} context - Additional context
 * @returns {Object} Standardized error response
 */
export const handleRBACError = (error, operation = '', context = {}) => {
    const rbacContext = `RBAC ${operation}`;
    
    // Add RBAC specific context
    const additionalData = {
        operation,
        userId: context.userId,
        roleId: context.roleId,
        permission: context.permission,
        ...context
    };
    
    return handleApiError(error, rbacContext, {
        additionalData,
        fallbackMessage: `Failed to ${operation}. Please check your permissions and try again.`
    });
};

/**
 * Handle user management errors
 * @param {Object} error - Error object
 * @param {string} operation - User operation that failed
 * @param {Object} context - Additional context
 * @returns {Object} Standardized error response
 */
export const handleUserManagementError = (error, operation = '', context = {}) => {
    const userContext = `User Management ${operation}`;
    
    const additionalData = {
        operation,
        targetUserId: context.targetUserId,
        currentUserId: context.currentUserId,
        ...context
    };
    
    return handleApiError(error, userContext, {
        additionalData,
        fallbackMessage: `Failed to ${operation}. Please check your permissions and try again.`
    });
};

/**
 * Enhanced retry mechanism for failed operations with exponential backoff
 * @param {Function} operation - Operation to retry
 * @param {Object} options - Retry options
 * @returns {Promise} Operation result
 */
export const retryOperation = async (operation, options = {}) => {
    const {
        maxRetries = 3,
        initialDelay = 1000,
        maxDelay = 10000,
        exponentialBackoff = true,
        retryCondition = null,
        onRetry = null,
        context = 'unknown operation'
    } = options;
    
    let lastError;
    let delay = initialDelay;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const result = await operation();
            
            // If we get here, operation succeeded
            if (attempt > 1) {
                console.info(`✅ ${context} succeeded on attempt ${attempt}`);
            }
            
            return result;
        } catch (error) {
            lastError = error;
            
            const errorType = getErrorType(error);
            
            // Check if this error should be retried
            const shouldRetry = retryCondition ? 
                retryCondition(error, attempt) : 
                isRetryableError(errorType);
            
            if (!shouldRetry) {
                console.warn(`🚫 ${context} failed with non-retryable error: ${errorType}`);
                throw error;
            }
            
            if (attempt === maxRetries) {
                logError(error, `${context} failed after ${maxRetries} attempts`);
                console.error(`❌ ${context} failed permanently after ${maxRetries} attempts`);
                throw error;
            }
            
            // Log retry attempt
            console.warn(`🔄 ${context} failed on attempt ${attempt}/${maxRetries}, retrying in ${delay}ms...`);
            
            // Call onRetry callback if provided
            if (onRetry && typeof onRetry === 'function') {
                try {
                    await onRetry(error, attempt);
                } catch (callbackError) {
                    console.warn('Retry callback failed:', callbackError);
                }
            }
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay));
            
            // Calculate next delay with exponential backoff
            if (exponentialBackoff) {
                delay = Math.min(delay * 2, maxDelay);
            }
        }
    }
    
    throw lastError;
};

/**
 * Check if an error type is retryable
 * @param {string} errorType - The error type
 * @returns {boolean} Whether the error is retryable
 */
const isRetryableError = (errorType) => {
    const retryableErrors = [
        ERROR_TYPES.NETWORK,
        ERROR_TYPES.SERVER,
        ERROR_TYPES.UNKNOWN
    ];
    return retryableErrors.includes(errorType);
};

/**
 * Create a retry wrapper for async functions
 * @param {Function} asyncFunction - The async function to wrap
 * @param {Object} retryOptions - Retry configuration
 * @returns {Function} Wrapped function with retry logic
 */
export const withRetry = (asyncFunction, retryOptions = {}) => {
    return async (...args) => {
        return retryOperation(
            () => asyncFunction(...args),
            {
                context: asyncFunction.name || 'anonymous function',
                ...retryOptions
            }
        );
    };
};

/**
 * Auto-retry wrapper for API calls with smart error handling
 * @param {Function} apiCall - The API call function
 * @param {Object} options - Configuration options
 * @returns {Promise} API call result
 */
export const apiCallWithRetry = async (apiCall, options = {}) => {
    const {
        showLoading = true,
        showError = true,
        context = 'API call',
        retryOptions = {},
        loadingCallback = null,
        errorCallback = null
    } = options;
    
    let loadingToastId = null;
    
    try {
        // Show loading toast if enabled
        if (showLoading && loadingCallback) {
            loadingToastId = loadingCallback('Loading...');
        }
        
        const result = await retryOperation(apiCall, {
            context,
            onRetry: (error, attempt) => {
                if (showLoading && loadingCallback) {
                    loadingCallback(`Retrying... (${attempt}/3)`);
                }
            },
            ...retryOptions
        });
        
        // Dismiss loading toast on success
        if (loadingToastId && toast.dismiss) {
            toast.dismiss(loadingToastId);
        }
        
        return result;
        
    } catch (error) {
        // Dismiss loading toast on error
        if (loadingToastId && toast.dismiss) {
            toast.dismiss(loadingToastId);
        }
        
        // Handle error
        if (showError) {
            const errorResult = handleApiError(error, context, {
                enableRetry: true,
                retryCallback: () => apiCallWithRetry(apiCall, options)
            });
            
            if (errorCallback && typeof errorCallback === 'function') {
                errorCallback(errorResult);
            }
        }
        
        throw error;
    }
};

/**
 * Parse Pydantic validation errors from backend
 * @param {Object} error - Error object from API
 * @returns {Object} Parsed field errors and first error info
 */
export const parsePydanticValidationErrors = (error) => {
    const detail = error?.response?.data?.detail;

    // Check if detail is an array of validation errors (Pydantic format)
    if (!Array.isArray(detail) || detail.length === 0) {
        return {
            hasValidationErrors: false,
            fieldErrors: {},
            firstError: null
        };
    }

    const fieldErrors = {};
    let firstError = null;

    detail.forEach((validationError, index) => {
        const { loc, msg, type } = validationError;

        // Extract field name from location path
        // loc is like ["body", "demographics", "primary_physician"]
        // We want the last element as the field name
        const fieldName = loc && Array.isArray(loc) ? loc[loc.length - 1] : null;

        if (fieldName && msg) {
            // Store the error message for this field
            if (!fieldErrors[fieldName]) {
                fieldErrors[fieldName] = msg;

                // Store the first error for scrolling/focus purposes
                if (!firstError) {
                    firstError = {
                        field: fieldName,
                        message: msg,
                        type: type,
                        fullPath: loc
                    };
                }
            }
        }
    });

    return {
        hasValidationErrors: Object.keys(fieldErrors).length > 0,
        fieldErrors,
        firstError,
        totalErrors: detail.length
    };
};

/**
 * Validate response structure
 * @param {Object} response - API response
 * @param {Array} requiredFields - Required fields in response
 * @returns {Object} Validation result
 */
export const validateResponse = (response, requiredFields = []) => {
    if (!response) {
        return {
            isValid: false,
            message: 'No response received',
            missingFields: requiredFields
        };
    }
    
    const missingFields = requiredFields.filter(field => {
        const keys = field.split('.');
        let current = response;
        
        for (const key of keys) {
            if (current && typeof current === 'object' && key in current) {
                current = current[key];
            } else {
                return true; // Field is missing
            }
        }
        
        return false; // Field exists
    });
    
    return {
        isValid: missingFields.length === 0,
        message: missingFields.length > 0 ? `Missing required fields: ${missingFields.join(', ')}` : 'Valid response',
        missingFields
    };
};

// Export default error handler
export default {
    handleApiError,
    handleRBACError,
    handleUserManagementError,
    retryOperation,
    validateResponse,
    parsePydanticValidationErrors,
    logError,
    getUserFriendlyMessage,
    getErrorType,
    getErrorSeverity,
    ERROR_TYPES,
    ERROR_SEVERITY
};